import { h, Fragment } from 'preact';
import { forwardRef } from 'preact/compat';
import { formatMoney } from 'utils/storefront/format-resource';
import { useEffect, useMemo, useRef } from 'preact/hooks';
import Layout from 'utils/layout';
import useInView from 'utils/in-view';
import VariantSelector from '../product/variant-selector';
import ProductStickyButton from '../product/product-sticky-button';
import extractAndFormat from '../utils/extract-and-format';

const AddToCartButton = ({ currentVariant, isQuickView, qty, kitSet, url }) => {
  const buttonClasses = isQuickView
    ? 'h-10 items-center py-3 px-3 label-10 uppercase flex justify-between w-full border border-supergoop-blue text-supergoop-blue mt-auto'
    : 'button button-primary uppercase label-12 tracking-[2px] w-full !mb-[10px]';
  const handleAddToCart = (e) => {
    e.preventDefault();

    const addToCartEvent = new CustomEvent('cart:add', {
      detail: {
        payload: {
          merchandiseId: currentVariant.id,
          quantity: qty,
        },
      },
    });
    document.dispatchEvent(addToCartEvent);
  };

  return (
    <>
      {kitSet ? (
        <a
          className="h-10 no-underline items-center py-3 px-3 label-10 uppercase flex justify-center w-full border border-grey-2A text-grey-2A focus:border-supergoop-blue peer-[.test]:border-supergoop-blue focus:text-supergoop-blue lg:border-supergoop-blue lg:text-supergoop-blue mt-auto"
          disabled={!currentVariant.availableForSale}
          href={url}
        >
          <span>
            {currentVariant
              ? window.TMW.Strings.product.shopNow
              : window.TMW.Strings.product.unavailable}
          </span>
        </a>
      ) : (
        <button
          type="submit"
          className={buttonClasses}
          disabled={!currentVariant.availableForSale}
          onClick={(e) => handleAddToCart(e)}
        >
          <span>
            {currentVariant
              ? currentVariant?.availableForSale
                ? window.TMW.Strings.product.addToCart
                : window.TMW.Strings.product.outOfStock
              : window.TMW.Strings.product.unavailable}
          </span>
          <span>
            {!isQuickView && ' - '}{' '}
            {extractAndFormat(
              formatMoney({ amount: currentVariant.price.amount * qty }),
            )}
          </span>
        </button>
      )}
    </>
  );
};

const ProductForm = forwardRef((props, ref) => {
  const {
    kitSet,
    url,
    galleryInView,
    product,
    showStickyAtc,
    isQuickView,
    currentVariant,
    setCurrentVariant,
  } = props;
  const [formContainer, formInView] = useInView();
  const qty = 1;
  const isStickyVisible = useMemo(() => {
    const layout = Layout.getInstance();
    if (layout.isBelowBreakpoint('lg')) {
      return !formInView && !galleryInView;
    }
    return !formInView;
  }, [formInView, galleryInView]);
  const variantSelectors = useMemo(
    () =>
      !props.product.hasOnlyDefaultVariant &&
      props.product.options.map((option) => (
        <VariantSelector
          currentVariant={currentVariant}
          key={option.name}
          option={option}
          setCurrentVariant={setCurrentVariant}
          variants={props.product.variants}
          product={props.product}
          isQuickView={isQuickView}
          isGiftCard
        />
      )),
    [currentVariant],
  );
  const addToCartButton = useMemo(
    () => (
      <AddToCartButton
        currentVariant={currentVariant}
        isQuickView={isQuickView}
        qty={qty}
        kitSet={kitSet}
        url={url}
      />
    ),
    [currentVariant, qty],
  );

  const isInitialMount = useRef(true);

  useEffect(() => {
    if (window.location && window.location.toString().includes('products/')) {
      const searchParams = new URLSearchParams(window.location.search);
      const id = currentVariant.id.split('/ProductVariant/')[1];
      searchParams.set('variant', id);
      const newRelativePathQuery = `${window.location.pathname
        }?${searchParams.toString()}`;
      if (isInitialMount.current) {
        window.history.replaceState(null, '', newRelativePathQuery);
        isInitialMount.current = false;
      } else {
        window.history.replaceState(null, '', newRelativePathQuery);
      }
    }
  }, [currentVariant]);

  return (
    <>
      <div ref={formContainer}>
        <form
          method="post"
          action="/cart/add"
          acceptCharset="UTF-8"
          encType="multipart/form-data"
          noValidate="novalidate"
          data-product-form=""
          data-has-only-default-variant={
            props.product.variants.length > 1 ? true : ''
          }
          className={`${props.product.variants.length > 1 && ' '}  ${!!isQuickView && 'lg:hidden'
            } w-full bg-white top-full z-10`}
        >
          <div ref={ref}>{variantSelectors}</div>
          {addToCartButton}
        </form>
      </div>
      {showStickyAtc && (
        <ProductStickyButton
          product={product}
          variant={currentVariant}
          options={product.options}
          isVisible={isStickyVisible}
        >
          {addToCartButton}
        </ProductStickyButton>
      )}
    </>
  );
});

export default ProductForm;
